import { useEffect, useMemo, useState, type ReactElement } from 'react';

import {
  ApiError,
  createContractorApi,
  type ContractorRevalidationDto,
  type JsonValue,
} from './api';
import { readContractorCapability } from './capability';
import {
  buildUpdateRevalidationRequest,
  clientVisibleRequiredIssues,
  initializeEmptyDocumentEdits,
  initializeEmptyFieldEdits,
  revalidationFormSchema,
  type DocumentEditState,
  type FieldEditState,
} from './contractor-questionnaire-model';
import { DynamicQuestionnaire } from './contractor-questionnaire';
import { revalidationStatusLabel } from './contractor-presentation';
import {
  ContractorCard,
  ContractorIntro,
  ContractorShell,
  ContractorTechnicalBlock,
} from './contractor-shell';

const CLOSED = new Set([
  'CAPABILITY_INVALID',
  'CAPABILITY_EXPIRED',
  'CAPABILITY_REVOKED',
  'CAPABILITY_CLOSED',
]);

const CLOSED_COPY: Record<string, string> = {
  CAPABILITY_INVALID: 'Ссылка недействительна. Доступ к дозаполнению закрыт.',
  CAPABILITY_EXPIRED: 'Срок действия ссылки истёк. Доступ к дозаполнению закрыт.',
  CAPABILITY_REVOKED: 'Доступ по ссылке отозван. Редактирование недоступно.',
  CAPABILITY_CLOSED: 'Дозаполнение закрыто. Повторный просмотр по этой ссылке недоступен.',
};

const REVALIDATION_LEAD =
  'Требования к осмотру были обновлены. Заполните только новые обязательные данные. Ранее переданное предзаполнение здесь повторно не открывается.';

export function ContractorRevalidationApp(props: { readonly apiBaseUrl: string }): ReactElement {
  const api = useMemo(() => createContractorApi(props.apiBaseUrl), [props.apiBaseUrl]);
  const [dto, setDto] = useState<ContractorRevalidationDto | null>(null);
  const [fieldEdits, setFieldEdits] = useState<FieldEditState>({});
  const [documentEdits, setDocumentEdits] = useState<DocumentEditState>({});
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const [documentErrors, setDocumentErrors] = useState<Map<string, string>>(new Map());
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [closed, setClosed] = useState<string | null>(
    readContractorCapability() === null ? 'CAPABILITY_INVALID' : null,
  );

  useEffect(() => {
    if (closed !== null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await api.getRevalidation();
        if (cancelled) {
          return;
        }
        setDto(loaded);
        setFieldEdits(initializeEmptyFieldEdits(loaded.fields));
        setDocumentEdits(initializeEmptyDocumentEdits(loaded.document_requirements));
        setLoadError(null);
      } catch (error) {
        if (!cancelled) {
          applyError(error, { wipeForm: true });
          if (!(
            error instanceof ApiError &&
            error.problem.code !== undefined &&
            CLOSED.has(error.problem.code)
          )) {
            setLoadError('Не удалось загрузить данные дозаполнения.');
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, closed]);

  const applyError = (error: unknown, options: { wipeForm?: boolean } = {}) => {
    if (
      error instanceof ApiError &&
      error.problem.code !== undefined &&
      CLOSED.has(error.problem.code)
    ) {
      if (options.wipeForm === true) {
        setDto(null);
      }
      setClosed(error.problem.code);
      return;
    }
    if (error instanceof ApiError && error.problem.code === 'STALE_VERSION') {
      // Preserve local edits. Do NOT assign case_version from Problem.current_version.
      setConflict(true);
      setMessage(
        'Данные на сервере изменились. Ваши правки сохранены в форме — обновите версии с сервера и повторите сохранение.',
      );
      return;
    }
    if (error instanceof ApiError && error.problem.code === 'VALIDATION_ERROR') {
      const nextFieldErrors = new Map<string, string>();
      const nextDocumentErrors = new Map<string, string>();
      for (const row of error.problem.errors ?? []) {
        if (row.field.includes('::')) {
          nextDocumentErrors.set(row.field.split('.')[0]!, row.message ?? row.code);
        } else {
          nextFieldErrors.set(row.field, row.message ?? row.code);
        }
      }
      setFieldErrors(nextFieldErrors);
      setDocumentErrors(nextDocumentErrors);
      setMessage(
        (error.problem.errors ?? []).map((row) => row.message ?? row.field).join(' ') ||
          'Проверьте обязательные поля.',
      );
      return;
    }
    if (error instanceof ApiError && error.problem.code === 'CONFIGURATION_REVALIDATION_REQUIRED') {
      setMessage(
        'Требования к осмотру были обновлены. Заполните только новые обязательные данные.',
      );
      return;
    }
    setMessage('Не удалось выполнить запрос.');
  };

  const refreshAuthoritativeVersions = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const loaded = await api.getRevalidation();
      setDto(loaded);
      // Keep local field/document edits (no last-write-wins wipe).
      setConflict(false);
      setFieldErrors(new Map());
      setDocumentErrors(new Map());
      setMessage(
        'Версии обновлены с сервера. Проверьте данные и повторите сохранение или отправку.',
      );
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (dto === null || dto.status !== 'OPEN') {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const body = buildUpdateRevalidationRequest({
        caseVersion: dto.case_version,
        revalidationVersion: dto.version,
        configurationFingerprint: dto.required_configuration_fingerprint,
        fields: dto.fields,
        documents: dto.document_requirements,
        fieldEdits,
        documentEdits,
      });
      const updated = await api.updateRevalidation(body);
      setDto(updated);
      setConflict(false);
      setFieldErrors(new Map());
      setDocumentErrors(new Map());
      setMessage('Данные сохранены.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (dto === null || dto.status !== 'OPEN') {
      return;
    }
    const schema = revalidationFormSchema({
      fingerprint: dto.required_configuration_fingerprint,
      fields: dto.fields,
      documents: dto.document_requirements,
    });
    const issues = clientVisibleRequiredIssues({
      schema,
      documents: dto.document_requirements,
      fieldEdits,
      documentEdits,
    });
    if (issues.length > 0) {
      setMessage(`Не заполнено: ${issues.join(', ')}`);
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const updateBody = buildUpdateRevalidationRequest({
        caseVersion: dto.case_version,
        revalidationVersion: dto.version,
        configurationFingerprint: dto.required_configuration_fingerprint,
        fields: dto.fields,
        documents: dto.document_requirements,
        fieldEdits,
        documentEdits,
      });
      const saved = await api.updateRevalidation(updateBody);
      const submitted = await api.submitRevalidation({
        expected_case_version: saved.case_version,
        expected_revalidation_version: saved.version,
        configuration_fingerprint: saved.required_configuration_fingerprint,
      });
      setDto(submitted);
      setConflict(false);
      setMessage('Дозаполнение завершено. Данные переданы.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  if (closed !== null) {
    return (
      <main className="page page-contractor">
        <ContractorShell statusLabel="Доступ закрыт" statusTone="status--danger">
          <ContractorIntro
            eyebrow="Дозаполнение до осмотра"
            title="Дозаполнение перед осмотром"
            lead={REVALIDATION_LEAD}
          />
          <p role="alert" className="banner banner-closed">
            {CLOSED_COPY[closed] ?? CLOSED_COPY.CAPABILITY_CLOSED}
          </p>
        </ContractorShell>
      </main>
    );
  }

  if (dto === null) {
    return (
      <main className="page page-contractor">
        <ContractorShell>
          <ContractorIntro
            eyebrow="Дозаполнение до осмотра"
            title="Дозаполнение перед осмотром"
            lead={REVALIDATION_LEAD}
          />
          <p role="status">{loadError ?? 'Загрузка…'}</p>
        </ContractorShell>
      </main>
    );
  }

  const readOnly = dto.status !== 'OPEN';
  const schema = revalidationFormSchema({
    fingerprint: dto.required_configuration_fingerprint,
    fields: dto.fields,
    documents: dto.document_requirements,
  });

  return (
    <main className="page page-contractor">
      <ContractorShell
        statusLabel={revalidationStatusLabel(dto.status)}
        statusTone={dto.status === 'RESOLVED' ? 'status--active' : 'status--warning'}
      >
        <ContractorIntro
          eyebrow="Дозаполнение до осмотра"
          title="Дозаполнение перед осмотром"
          lead={REVALIDATION_LEAD}
        />

        {conflict ? (
          <div role="alert" className="banner banner-conflict">
            <p>Конфликт версии. Правки не отправлены и не удалены.</p>
            <p className="meta">
              Данные были изменены на сервере. Обновите актуальные версии и повторите отправку.
            </p>
            {!readOnly ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  void refreshAuthoritativeVersions();
                }}
              >
                Обновить версии с сервера
              </button>
            ) : null}
          </div>
        ) : null}
        {message !== null ? (
          <p role="status" className="banner">
            {message}
          </p>
        ) : null}

        <ContractorCard
          title="Новые обязательные данные"
          titleId="revalidation-delta-heading"
          description="Отображаются только новые требования. Ранее переданные данные предзаполнения здесь не открываются повторно."
        >
          <DynamicQuestionnaire
            schema={schema}
            documents={dto.document_requirements}
            fieldEdits={fieldEdits}
            documentEdits={documentEdits}
            fieldErrors={fieldErrors}
            documentErrors={documentErrors}
            readOnly={readOnly || busy}
            busy={busy}
            documentsHeading="Документы и их реквизиты"
            onFieldChange={(fieldKey, value) => {
              setFieldEdits((prev) => ({ ...prev, [fieldKey]: value }));
            }}
            onDocumentPresentChange={(documentKey, present) => {
              setDocumentEdits((prev) => ({
                ...prev,
                [documentKey]: {
                  present,
                  metadata: prev[documentKey]?.metadata ?? {},
                },
              }));
            }}
            onDocumentMetadataChange={(documentKey, fieldCode, value: JsonValue | undefined) => {
              setDocumentEdits((prev) => {
                const existing = prev[documentKey] ?? { present: true, metadata: {} };
                const metadata = { ...existing.metadata };
                if (value === undefined) {
                  delete metadata[fieldCode];
                } else {
                  metadata[fieldCode] = value;
                }
                return {
                  ...prev,
                  [documentKey]: { present: true, metadata },
                };
              });
            }}
          />
        </ContractorCard>

        {readOnly ? (
          <p role="status" className="banner banner-success">
            {dto.status === 'RESOLVED'
              ? 'Дозаполнение завершено. Данные переданы.'
              : revalidationStatusLabel(dto.status)}
          </p>
        ) : (
          <div className="contractor-actions actions">
            <div className="contractor-actions-in">
              <p className="contractor-actions-hint">
                {conflict
                  ? 'Сначала обновите версии с сервера'
                  : 'Черновик · изменения не отправлены до нажатия «Отправить данные»'}
              </p>
              <div className="contractor-actions-buttons">
                <button type="button" onClick={() => void save()} disabled={busy || conflict}>
                  Сохранить черновик
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => void submit()}
                  disabled={busy || conflict}
                >
                  Отправить данные
                </button>
              </div>
            </div>
          </div>
        )}

        <ContractorTechnicalBlock>
          <p>Статус: {dto.status}</p>
          <p>Версия требования: {dto.version}</p>
          <p>Версия осмотра: {dto.case_version}</p>
        </ContractorTechnicalBlock>
      </ContractorShell>
    </main>
  );
}
